import { useLingui } from "@lingui/react/macro";
import { AppShell, Box, Divider, Group, rem, Stack, Text } from "@mantine/core";
import { useHeadroom } from "@mantine/hooks";
import { useDrag } from "@use-gesture/react";
import { observer } from "mobx-react-lite";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher/LanguageSwitcher";
import { ButtonBurguer } from "@/components/common/ui/button/ButtonBurger/ButtonBurguer";
import { ButtonBurguerThumb } from "@/components/common/ui/button/ButtonBurger/ButtonBurguerThumb";
import Logo from "@/components/common/ui/datadisplay/Logo";
import { crewStore, uiStore, userStore } from "@/mobx/rootStore";
import { useAppSizeId } from "@/utils/hooks/useAppSizeId";
import { useEventsConnection } from "@/utils/sse/useEventsConnection";
import { version } from "../../../package.json";
import classes from "./PrivateLayout.module.css";
import AppSelector from "./partials/AppSelector";
import CrewSelector from "./partials/CrewSelector";
import UserInfo from "./partials/UserInfo";

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export default observer(function PrivateLayout({ children }) {
	const { t } = useLingui();
	const { pinned } = useHeadroom({ fixedAt: 120 });
	const { isMobile } = useAppSizeId();

	useEventsConnection();

	const optThumbBurger = userStore.getSetting("opt_thumbBurger");
	const optGroupShortcut = userStore.getSetting("opt_groupShortcut");

	const bind = useDrag(
		({ swipe, tap }) => {
			if (tap) {
				return;
			}

			const swipeX = swipe[0]; // position will either be -1, 0 or 1

			if (swipeX === 0) {
				return;
			}
			if (swipeX === -1) {
				uiStore.closeMobileNavbar();
			}
			if (swipeX === 1) {
				uiStore.openMobileNavbar();
			}
		},
		{
			filterTaps: true,
			axis: "x",
			swipe: {
				distance: 20,
				velocity: 1,
				duration: 1500,
			},
		},
	);

	const thumbBurgerStyle = {
		left: {
			bottom: "var(--mantine-spacing-xxs)",
			left: "var(--mantine-spacing-sm)",
		},
		right: {
			bottom: "var(--mantine-spacing-xxs)",
			right: "var(--mantine-spacing-sm)",
		},
	}[optThumbBurger];

	const hasCrews = crewStore.hasCrews();

	const isOpened = uiStore.getIsNavbarOpened();

	const isThumbBurgerEnabled = optThumbBurger !== "hidden";

	let shouldShowThumbBurger = true;

	if (optThumbBurger === "left") {
		shouldShowThumbBurger = isThumbBurgerEnabled && !isOpened;
	} else if (!isThumbBurgerEnabled) {
		shouldShowThumbBurger = false;
	}

	return (
		<AppShell
			padding="md"
			header={{
				height: 60,
				collapsed: !isOpened && !pinned,
				offset: true,
			}}
			navbar={{
				width: 300,
				breakpoint: "sm",
				collapsed: { mobile: !isOpened, desktop: !isOpened },
			}}
			className={classes.appShell}
			{...bind()}
			data-swipe-action="openMenu"
		>
			<AppShell.Header>
				<Group h="100%" w="100%" px="md" wrap="nowrap">
					<ButtonBurguer opened={isOpened} onClick={() => uiStore.toggleNavbar()} />

					{(isOpened || !isMobile) && (
						<>
							<Group gap="xs">
								<Logo />

								<Text size="lg" fw="bold">
									Economos
								</Text>
							</Group>

							<Text size="sm" c="dimmed">
								{version}
							</Text>
						</>
					)}

					{isMobile && !uiStore.getIsNavbarOpened() && optGroupShortcut === "visible" && (
						<CrewSelector variant="mobile" />
					)}

					<LanguageSwitcher ml="auto" />
				</Group>
			</AppShell.Header>

			<AppShell.Navbar p="md">
				<Stack
					gap="xl"
					h="100%"
					style={{ overflowY: "auto" }}
					data-swipe-action="closeMenu"
				>
					<CrewSelector />
					<Divider />
					{hasCrews && <AppSelector closeMobileFnc={uiStore.closeNavbar} />}

					<Divider />

					<UserInfo />

					<Divider />

					<Box pl="xs">
						<ButtonBurguer
							opened={isOpened}
							onClick={() => uiStore.toggleNavbar()}
							text={t`Cerrar menú`}
						/>
					</Box>
				</Stack>
			</AppShell.Navbar>

			<div className={classes.thumbBurgerContainer} style={thumbBurgerStyle}>
				{shouldShowThumbBurger && (
					<ButtonBurguerThumb opened={isOpened} onClick={() => uiStore.toggleNavbar()} />
				)}
			</div>

			<AppShell.Main pt={`calc(${rem(60)} + var(--mantine-spacing-md))`}>
				{children}
			</AppShell.Main>
		</AppShell>
	);
});
